import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCamera, FaSpinner, FaCheckCircle, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  sellerAvailabilityService,
  authService,
  resolveProductMediaUrl,
} from '../../../services/apiService';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AvailabilityRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [activeOrderId, setActiveOrderId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await sellerAvailabilityService.listRequests();
      setRequests(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const pending = requests.filter((r) => r.status === 'awaiting_seller_photo');
  const completed = requests.filter((r) => r.status === 'photo_received');

  const handlePickPhoto = (orderId) => {
    setActiveOrderId(orderId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeOrderId) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingId(activeOrderId);
    try {
      const base64 = await fileToBase64(file);
      const uploaded = await authService.uploadFile(
        base64,
        file.name,
        file.type
      );
      const photoUrl = uploaded;
      if (!photoUrl) throw new Error('Upload failed');

      await sellerAvailabilityService.submitVerificationPhoto(activeOrderId, photoUrl);
      toast.success('Verification photo sent to buyer');
      await load();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingId(null);
      setActiveOrderId(null);
    }
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <FaSpinner className="mr-2 animate-spin" /> Loading availability requests…
      </div>
    );
  }

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 sm:text-lg">Check availability requests</h2>
          <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">
            Buyers asked you to verify a used part in stock. Upload a photo of the actual part.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
        >
          Refresh
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:gap-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 sm:text-xs">
            Awaiting your photo
          </p>
          <p className="mt-0.5 text-lg font-semibold text-amber-900 sm:text-xl">{pending.length}</p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800 sm:text-xs">
            Photo sent
          </p>
          <p className="mt-0.5 text-lg font-semibold text-emerald-900 sm:text-xl">{completed.length}</p>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-gray-500">
          No check availability requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const isPending = req.status === 'awaiting_seller_photo';
            const img = resolveProductMediaUrl(req.imageUrl);
            const shortOrderId = req.orderId?.replace(/^ORD/i, '') || req.orderId;
            return (
              <div
                key={req.orderId}
                className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-4"
              >
                <div className="flex gap-3 sm:gap-4">
                  <img
                    src={img || 'https://via.placeholder.com/64'}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-md border border-gray-200 object-cover sm:h-16 sm:w-16"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <h3 className="text-sm font-semibold leading-snug text-gray-900 sm:text-base">
                        {req.partName}
                      </h3>
                      {req.gradeLabel && (
                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 sm:text-xs">
                          {req.gradeLabel}
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium sm:text-xs ${
                          isPending
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {isPending ? (
                          <>
                            <FaClock className="text-[10px]" /> Awaiting photo
                          </>
                        ) : (
                          <>
                            <FaCheckCircle className="text-[10px]" /> Photo sent
                          </>
                        )}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Order #{shortOrderId}
                      {req.buyer?.name ? ` · ${req.buyer.name}` : ''}
                    </p>
                    <p className="text-xs text-gray-400">
                      Requested{' '}
                      {req.requestedAt
                        ? new Date(req.requestedAt).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </p>

                    {req.verificationPhotoUrl && (
                      <div className="mt-2">
                        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400 sm:text-xs">
                          Your photo
                        </p>
                        <img
                          src={resolveProductMediaUrl(req.verificationPhotoUrl)}
                          alt="Verification"
                          className="max-h-24 rounded-md border border-gray-200 sm:max-h-28"
                        />
                      </div>
                    )}

                    {isPending && (
                      <button
                        type="button"
                        onClick={() => handlePickPhoto(req.orderId)}
                        disabled={uploadingId === req.orderId}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50 sm:text-sm"
                      >
                        {uploadingId === req.orderId ? (
                          <>
                            <FaSpinner className="animate-spin text-xs" /> Uploading…
                          </>
                        ) : (
                          <>
                            <FaCamera className="text-xs" /> Upload photo
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
